/**
 * 公共图片压缩工具（先压缩、再上传）
 * 默认策略：不修改原图尺寸，仅按指定质量重新编码（JPEG）降低体积
 * 提供 File/Blob 压缩（输出 File，供 FormData 上传）
 * 与 base64(dataURL) 压缩（输出 dataURL，供 base64 流上传）两种入口
 *
 * options:
 *   maxEdge  可选最大边长，传入数值时长边超出才等比缩小；默认 null 不缩放、保持原尺寸
 *   quality  重新编码质量 0~1，默认 0.8
 *   mimeType 输出格式，默认 'image/jpeg'（需保留透明的场景可传 'image/png'）
 *   maxSize  压缩触发阈值（字节），仅当文件超过该体积才压缩，默认 8MB
 *   timeout  处理超时时间（毫秒），超过则报错"处理失败"并返回 null，默认 15000
 */

// 默认压缩配置
const DEFAULT_OPTIONS = {
  maxEdge: null, // 默认不修改尺寸，仅重新编码降体积
  quality: 0.8,
  mimeType: 'image/jpeg',
  maxSize: 1024 * 1024 * 8, // 超过 8MB 才压缩，不超过直接返回原文件
  timeout: 15000 // 处理超过 15s 视为失败
}

// 合并配置（过滤 undefined，避免覆盖默认值）
const mergeOptions = options => {
  const opts = Object.assign({}, DEFAULT_OPTIONS)
  if (options) {
    Object.keys(options).forEach(key => {
      if (options[key] !== undefined) {
        opts[key] = options[key]
      }
    })
  }
  return opts
}

// 加载 Image 对象（支持 File/Blob 的 objectURL 或 dataURL）
const loadImage = src =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'Anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('图片加载失败'))
    img.src = src
  })

// 计算输出尺寸：默认保持原尺寸；传入 maxEdge 时长边超出才等比缩小
const calculateSize = (img, maxEdge) => {
  let width = img.width
  let height = img.height
  if (maxEdge) {
    const longerEdge = Math.max(width, height)
    if (longerEdge > maxEdge) {
      const scale = maxEdge / longerEdge
      width = Math.round(width * scale)
      height = Math.round(height * scale)
    }
  }
  return { width, height }
}

// 按指定尺寸绘制到 canvas（JPEG 铺白底避免透明区域变黑）
const drawToCanvas = (img, width, height, mimeType) => {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  ctx.clearRect(0, 0, width, height)
  if (mimeType === 'image/jpeg') {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, width, height)
  }
  ctx.drawImage(img, 0, 0, width, height)
  return canvas
}

// 通过 canvas 按输出尺寸绘制并重新编码（默认原尺寸，仅降质量）
const drawCompressedCanvas = (img, options) => {
  const opts = mergeOptions(options)
  const { width, height } = calculateSize(img, opts.maxEdge)
  return drawToCanvas(img, width, height, opts.mimeType)
}

// 页面提示（框架全局 tuhu 可用时才提示，避免工具层硬依赖）
const toast = message => {
  try {
    if (typeof tuhu !== 'undefined' && tuhu.toast) {
      tuhu.toast(message)
    }
  } catch (e) {}
}

// 持续提示：处理完成前不会消失（Preloader 需手动关闭）
const showProcessing = () => {
  try {
    if (typeof tuhu !== 'undefined' && tuhu.showPreloader) {
      tuhu.showPreloader('图片较大，正在处理…')
    }
  } catch (e) {}
}

// 关闭持续提示
const hideProcessing = () => {
  try {
    if (typeof tuhu !== 'undefined' && tuhu.hidePreloader) {
      tuhu.hidePreloader()
    }
  } catch (e) {}
}

// canvas 转 blob
const canvasToBlob = (canvas, mimeType, quality) =>
  new Promise((resolve, reject) => {
    try {
      canvas.toBlob(
        blob => {
          blob ? resolve(blob) : reject(new Error('canvas 转换 blob 失败'))
        },
        mimeType,
        quality
      )
    } catch (e) {
      reject(e)
    }
  })

// 根据输出格式替换文件名后缀
const renameExt = (name, mimeType) => {
  const ext = (mimeType.split('/')[1] || 'jpg').toLowerCase()
  return name ? name.replace(/\.\w+$/, '') + '.' + ext : 'file.' + ext
}

// 体积格式化（字节 -> 可读字符串）
const formatSize = bytes => {
  if (bytes === null || bytes === undefined) return '未知'
  return bytes >= 1024 * 1024
    ? (bytes / 1024 / 1024).toFixed(2) + 'MB'
    : (bytes / 1024).toFixed(1) + 'KB'
}

// base64(dataURL) 近似字节数
const base64Bytes = dataUrl => {
  const idx = dataUrl.indexOf(',')
  const base64 = idx > -1 ? dataUrl.substring(idx + 1) : dataUrl
  return Math.round(base64.length * 3 / 4)
}

/**
 * 实际压缩流程：返回压缩后的 File，失败或体积未变小时返回原文件
 */
const doCompressFile = async (file, opts) => {
  let objectURL = null
  try {
    objectURL = URL.createObjectURL(file)
    const img = await loadImage(objectURL)

    // 第一轮：保持原尺寸，按指定质量编码
    let { width, height } = calculateSize(img, opts.maxEdge)
    let quality = opts.quality
    let blob = await canvasToBlob(
      drawToCanvas(img, width, height, opts.mimeType),
      opts.mimeType,
      quality
    )

    // 体积仍超阈值：逐级降低质量重试（仍保持原尺寸）
    if (blob && blob.size > opts.maxSize) {
      for (const q of [0.6, 0.4, 0.3]) {
        if (q >= quality) continue
        const retry = await canvasToBlob(
          drawToCanvas(img, width, height, opts.mimeType),
          opts.mimeType,
          q
        )
        if (retry && retry.size < blob.size) {
          blob = retry
          quality = q
        }
        if (blob.size <= opts.maxSize) break
      }
    }

    // 质量降到底仍超阈值：按体积估算等比缩小尺寸兜底（size ≈ blob.size × scale²）
    if (blob && blob.size > opts.maxSize) {
      for (let i = 0; i < 3 && blob.size > opts.maxSize; i++) {
        const scale = Math.max(0.1, Math.sqrt((opts.maxSize * 0.9) / blob.size))
        width = Math.max(1, Math.round(width * scale))
        height = Math.max(1, Math.round(height * scale))
        const retry = await canvasToBlob(
          drawToCanvas(img, width, height, opts.mimeType),
          opts.mimeType,
          quality
        )
        if (retry) blob = retry
      }
      console.log(
        `[图片压缩] ${file.name}: 原尺寸无法压到 ${formatSize(opts.maxSize)} 以下，已等比缩小尺寸兜底`
      )
    }

    if (blob && blob.size < file.size) {
      const compressed = new File([blob], renameExt(file.name, opts.mimeType), {
        type: opts.mimeType
      })
      console.log(
        `[图片压缩] ${file.name}: ${formatSize(file.size)} -> ${formatSize(compressed.size)}（${img.width}x${img.height} -> ${width}x${height}，${opts.mimeType}，quality=${quality}）`
      )
      if (compressed.size > opts.maxSize) {
        toast(`图片过大，压缩后仍超过 ${formatSize(opts.maxSize)}，建议更换较小的图片`)
      }
      return compressed
    }
    console.log(
      `[图片压缩] ${file.name}: ${formatSize(file.size)} -> ${formatSize(blob && blob.size)} 体积未变小，使用原文件`
    )
    return file
  } catch (e) {
    console.error('图片压缩失败，使用原文件上传', e)
    return file
  } finally {
    objectURL && URL.revokeObjectURL(objectURL)
  }
}

/**
 * 压缩 File/Blob，输出压缩后的 File，保证结果不超过 maxSize（默认 8MB）
 * 仅当文件超过 maxSize 才压缩；策略：优先保持原尺寸逐级降质量，
 * 仍超标时才按体积估算等比缩小尺寸兜底（仅极端大图会走到这一步）
 * 处理期间页面持续提示"图片较大，正在处理…"，完成/失败后关闭；
 * 超过 timeout（默认 15s）提示"处理失败"并返回 null，调用方应终止上传
 */
export const compressImage = async (file, options = {}) => {
  const opts = mergeOptions(options)

  // 未超过阈值不压缩，直接返回原文件（无耗时处理，不弹提示）
  if (file.size && file.size <= opts.maxSize) {
    console.log(
      `[图片压缩] ${file.name}: ${formatSize(file.size)} 未超过 ${formatSize(opts.maxSize)}，无需压缩`
    )
    return file
  }

  // 持续提示：处理完成/失败前不消失
  showProcessing()

  // 超时控制：超过 timeout 视为处理失败
  let timer = null
  const timeoutPromise = new Promise((resolve, reject) => {
    timer = setTimeout(() => reject(new Error('timeout')), opts.timeout)
  })

  try {
    return await Promise.race([doCompressFile(file, opts), timeoutPromise])
  } catch (e) {
    if (e && e.message === 'timeout') {
      console.error(
        `[图片压缩] ${file.name}: 处理超过 ${Math.round(opts.timeout / 1000)}s，已终止`
      )
      toast('图片处理失败，请重试')
      return null
    }
    console.error('图片压缩失败，使用原文件上传', e)
    return file
  } finally {
    timer && clearTimeout(timer)
    hideProcessing()
  }
}

/**
 * 压缩 base64(dataURL)，输出压缩后的 dataURL
 * 压缩失败时返回原 dataURL
 */
export const compressBase64Image = async (dataUrl, options = {}) => {
  const opts = mergeOptions(options)
  try {
    const img = await loadImage(dataUrl)
    const canvas = drawCompressedCanvas(img, opts)
    const result = canvas.toDataURL(opts.mimeType, opts.quality)
    console.log(
      `[图片压缩] base64图片: ${formatSize(base64Bytes(dataUrl))} -> ${formatSize(base64Bytes(result))}（${img.width}x${img.height} -> ${canvas.width}x${canvas.height}，${opts.mimeType}）`
    )
    return result
  } catch (e) {
    console.error('图片压缩失败，使用原图片上传', e)
    return dataUrl
  }
}

export { drawCompressedCanvas, formatSize, base64Bytes }

export default {
  compressImage,
  compressBase64Image,
  drawCompressedCanvas
}



// WEBPACK FOOTER //
// ./src/commons/imageCompress.js